import {
  SECTION_MESSAGE_TYPE,
  SectionEvent,
  SectionEventType,
  SectionStatusEntry,
  SectionStatusValue,
} from "~/types/section-streaming";

/** Lifecycle stages we recognize. Anything else is ignored (additive). */
const VALID_SECTION_EVENTS: ReadonlySet<string> = new Set<SectionEventType>([
  "started",
  "report_section",
  "completed",
  "error",
]);

/**
 * Default progressive message shown while sections are loading but no source
 * label is available (the section -> label mapping is an open design question).
 */
export const DEFAULT_SECTION_LOADING_MESSAGE = "Estamos consultando as fontes";

/**
 * Defensively parse a raw WebSocket payload into a typed {@link SectionEvent}.
 *
 * Returns `null` for anything that is not a recognized per-section event:
 * non-objects, a different/absent `message_type`, an unknown/missing `event`,
 * or a missing `section_id`. This keeps the consumer additive — unknown or
 * malformed events are skipped instead of throwing or mutating state.
 */
export function parseSectionEvent(payload: unknown): SectionEvent | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const obj = payload as Record<string, unknown>;

  if (obj.message_type !== SECTION_MESSAGE_TYPE) {
    return null;
  }

  const event = obj.event;
  if (typeof event !== "string" || !VALID_SECTION_EVENTS.has(event)) {
    return null;
  }

  const sectionId = obj.section_id;
  if (typeof sectionId !== "string" || sectionId.length === 0) {
    return null;
  }

  const label =
    typeof obj.section_label === "string" ? obj.section_label : undefined;
  const userReportsId =
    typeof obj.user_reports_id === "string" ? obj.user_reports_id : undefined;

  return {
    message_type: SECTION_MESSAGE_TYPE,
    event: event as SectionEventType,
    section_id: sectionId,
    section_label: label,
    user_reports_id: userReportsId,
    section: obj.section,
    incremental: obj.incremental === true,
  };
}

/** Map a section lifecycle stage to its UI status. */
export function sectionEventToStatus(
  event: SectionEventType
): SectionStatusValue {
  switch (event) {
    case "started":
    case "report_section":
      return "loading";
    case "completed":
      return "ready";
    case "error":
      return "error";
  }
}

/**
 * Build the progressive status message from the current section statuses.
 *
 * The message tracks the most recently started section that is still loading,
 * so it advances as each source is consulted (e.g. "Estamos consultando redes
 * sociais" then "Estamos consultando processos"). Returns `null` when no
 * section is loading, so the UI can hide the indicator.
 */
export function buildProgressiveMessage(
  statuses: Record<string, SectionStatusEntry>,
  order: readonly string[]
): string | null {
  const loadingIds = order.filter((id) => statuses[id]?.status === "loading");
  if (loadingIds.length === 0) {
    return null;
  }

  const lastLoadingId = loadingIds[loadingIds.length - 1];
  const label = statuses[lastLoadingId]?.label;

  return label
    ? `Estamos consultando ${label}`
    : DEFAULT_SECTION_LOADING_MESSAGE;
}

/** Ids of sections currently loading, in first-seen order (for skeletons). */
export function getLoadingSectionIds(
  statuses: Record<string, SectionStatusEntry>,
  order: readonly string[]
): string[] {
  return order.filter((id) => statuses[id]?.status === "loading");
}
